import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLingui } from '@lingui/react/macro'
import { authViewTransition, cn } from '@/lib/utils'
import { ButtonLink, buttonVariants } from '@/components/ui/button'
import { ContinueWithGoogle } from '@/routes/_app/-components/social-buttons'
import { IPhone } from './iphone'
import { ProfessionalTestScreen } from './mocks'
import { usePauseOffscreen } from './use-pause-offscreen'

// The sticky nav's height (h-17): a phone under it is not "visible".
const NAV_PX = 68

// How far down the screen (below the nav) the section's top has to rise before the phone comes in.
const ARRIVE_LINE = 0.8

/**
 * Whether the reader has scrolled into the professionals: the phone and its copy wait, hidden, until the section's top
 * rises past a line 80% of the way down the screen under the nav, so the phone spins in as soon as the section is
 * clearly in, without the reader having to scroll it up to the middle. On a screen tall enough to show the whole
 * section under the nav, it comes in right away. Rendered visible on the server, and under reduced motion it is simply
 * there.
 */
function useFullyVisible() {
  const ref = useRef<HTMLElement>(null)
  const [state, setState] = useState({ live: false, reached: false })

  useEffect(() => {
    const section = ref.current?.closest('section')
    if (!section || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    // oxlint-disable-next-line react/set-state-in-effect -- hydration parity: the server renders the phone visible; hiding it waits for the client
    setState((prev) => ({ live: true, reached: prev.reached }))
    const observers: IntersectionObserver[] = []
    const arrive = () => {
      for (const observer of observers) observer.disconnect()
      setState({ live: true, reached: true })
    }
    // The reader scrolled in: the section's top passed the arrival line (the root is the band from under the nav down
    // to that line, so the section enters it when its top crosses the line).
    observers.push(
      new IntersectionObserver((entries) => entries.some((entry) => entry.isIntersecting) && arrive(), {
        rootMargin: `-${NAV_PX}px 0px -${Math.round((1 - ARRIVE_LINE) * 100)}% 0px`,
      }),
    )
    // Or there is room for all of it: on a screen tall enough to show the whole section under the nav from the start
    // (or once a window is enlarged to it), it comes in without waiting for a scroll.
    observers.push(
      new IntersectionObserver((entries) => entries.some((entry) => entry.intersectionRatio >= 0.99) && arrive(), {
        rootMargin: `-${NAV_PX}px 0px 0px 0px`,
        threshold: [0.99],
      }),
    )
    for (const observer of observers) observer.observe(section)
    return () => {
      for (const observer of observers) observer.disconnect()
    }
  }, [])

  // `pending` until the script has taken over: the server's render stays hidden by CSS alone (`.pro-await`).
  return { ref, pending: !state.live, hidden: state.live && !state.reached, reached: state.reached }
}

function Rise(props: { hidden: boolean; delay: number; children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        // Hiding is instant (when the script takes over from the CSS-hidden first paint, nothing may fade out); only
        // the entrance animates.
        props.hidden ? 'translate-y-3 opacity-0' : 'transition-[opacity,translate] duration-500 ease-out',
        props.className,
      )}
      style={{ transitionDelay: props.hidden ? '0ms' : `${props.delay}ms` }}
    >
      {props.children}
    </div>
  )
}

// Pools of the gradient under the phone, flattened as if lying on the floor it stands on: outer first, each a little
// stronger than the one around it, so together they fade out from the phone's base.
const RINGS = [
  { rx: 280, ry: 54, opacity: 0.12 },
  { rx: 212, ry: 41, opacity: 0.16 },
  { rx: 150, ry: 29, opacity: 0.22 },
  { rx: 94, ry: 18, opacity: 0.32 },
]

/**
 * Soft, borderless rings of the landing's teal-to-violet under the base of the phone, in the floor's perspective: the
 * phone reads as standing in a pool of light. When it arrives they fade in, then breathe gently outward one after
 * another; without JavaScript or under reduced motion they are simply there.
 */
function PhoneWaves(props: { hidden: boolean; reached: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none absolute top-[96%] left-1/2 -z-10 h-[9rem] w-[38rem] max-w-none -translate-x-1/2 -translate-y-1/2',
        props.hidden && 'opacity-0',
        props.reached && 'pro-rings-in',
      )}
    >
      {/* One <svg> per ring, centred on the same point, so each breathes as a composited element (app.css). */}
      {RINGS.map((ring, index) => (
        <svg
          key={ring.rx}
          viewBox="-300 -70 600 140"
          className="absolute inset-0 size-full overflow-visible"
          style={{ '--i': index }}
        >
          <defs>
            <linearGradient
              id={`pro-ring-gradient-${index}`}
              gradientUnits="userSpaceOnUse"
              x1="-280"
              y1="-40"
              x2="280"
              y2="40"
            >
              <stop offset="0%" stopColor="hsl(163 99% 40%)" />
              <stop offset="55%" stopColor="hsl(190 90% 50%)" />
              <stop offset="100%" stopColor="hsl(242 100% 70%)" />
            </linearGradient>
          </defs>
          <ellipse rx={ring.rx} ry={ring.ry} fill={`url(#pro-ring-gradient-${index})`} fillOpacity={ring.opacity} />
        </svg>
      ))}
    </span>
  )
}

/** The professionals' strip, right under the hero: the iPhone spins in once all of it is in view, then the copy rises. */
export function ProfessionalsSection() {
  const { t } = useLingui()
  const { ref, pending, hidden, reached } = useFullyVisible()
  // The phone and its rings, not the whole section: the section's empty bottom padding can linger under the nav long
  // after the phone has scrolled away.
  const phoneRef = usePauseOffscreen<HTMLDivElement>()

  return (
    <section ref={ref} id="professionals" className="relative isolate scroll-mt-20 overflow-hidden">
      <div
        className={cn(
          'mx-auto grid max-w-6xl grid-cols-[minmax(0,1fr)] items-center gap-20 px-5 pt-14 pb-20 sm:px-6 sm:pb-24 md:grid-cols-[17rem_minmax(0,1fr)] md:gap-13 md:pt-16',
          pending && 'pro-await',
        )}
      >
        <div ref={phoneRef} className="relative justify-self-center py-4">
          <PhoneWaves hidden={hidden} reached={reached} />
          {/* The floor shadow stays flat while the phone turns above it. */}
          <span
            aria-hidden
            className="absolute inset-x-4 -bottom-1 h-8 rounded-[50%] bg-black/25 blur-xl dark:bg-black/60"
          />
          <IPhone
            label={t`For professionals`}
            // `phone-idle` holds the screen's demo still (app.css) until the phone arrives, so it starts as it spins in.
            className={cn(
              '[--phone-w:12.5rem] md:[--phone-w:14.5rem]',
              hidden && 'phone-idle opacity-0',
              reached && 'phone-spin',
            )}
          >
            {/* Plays the test and the report only: a finger ranks a triad, then the profile reads out. */}
            <ProfessionalTestScreen />
          </IPhone>
        </div>

        <div>
          <Rise hidden={hidden} delay={900}>
            <h2 className="max-w-[26ch] text-2xl leading-tight font-semibold tracking-tight text-balance sm:text-3xl">
              {t`It all starts with a 22-minute test`}
            </h2>
          </Rise>
          <Rise hidden={hidden} delay={1000}>
            <p className="text-muted-foreground mt-3.5 max-w-[40em] leading-relaxed">
              {t`If you're a professional, invest 22 minutes in your future. Fill in our intuitive, reliable questionnaire from any device and discover your unique profile of competences, values and personality.`}
            </p>
          </Rise>
          <Rise hidden={hidden} delay={1100} className="mt-6">
            {/* Addressed to the professional reader, so the page's company reader knows these buttons aren't for them. */}
            <p className="text-sm font-medium">{t`Do you want to take the test?`}</p>
          </Rise>
          <Rise hidden={hidden} delay={1150} className="mt-3 flex flex-wrap gap-3">
            {/* Straight into the professional's own registration: the reader asked about it has no account yet. */}
            <ButtonLink size="lg" to="/register/applicant" viewTransition={authViewTransition('enter')}>
              {t`Sign in`}
            </ButtonLink>
            {/* That page's own Google link; the server resolves where to land as if it started there. */}
            <ContinueWithGoogle
              redirect="/register/applicant"
              className={buttonVariants({ variant: 'outline', size: 'lg' })}
            />
          </Rise>
        </div>
      </div>
    </section>
  )
}
